import { useSyncExternalStore } from 'react';

/**
 * Rascunhos de mensagem por conversa, no padrão WhatsApp/Telegram: o texto
 * digitado e não enviado sobrevive à troca de conversa e ao recarregamento da
 * página, e a conversa aparece marcada na lista.
 *
 * Só texto. Anexos são `File`, que não têm serialização barata o bastante para
 * valer a pena aqui, então continuam se perdendo — de propósito.
 *
 * A chave é a `chatKey` (par canal + destinatário), a mesma identidade que o
 * resto da tela usa; ver o comentário em `chatList.ts` sobre por que índice de
 * array não serve.
 */

type Rascunho = { texto: string; atualizadoEm: number };
type Mapa = Record<string, Rascunho>;

/** Rascunho parado por mais que isso é descartado ao carregar a página. */
const VALIDADE_MS = 7 * 24 * 60 * 60 * 1000;

const VAZIO: Mapa = {};

// Namespace por usuário: navegador compartilhado não pode vazar rascunho de um
// atendente para o outro.
let chaveStorage: string | null = null;
let mapa: Mapa = VAZIO;

const ouvintes = new Set<() => void>();

const notificar = () => ouvintes.forEach(fn => fn());

function persistir() {
  if (!chaveStorage) return;

  try {
    localStorage.setItem(chaveStorage, JSON.stringify(mapa));
  } catch {
    // Storage cheio ou indisponível (aba anônima, política do navegador). O
    // rascunho é conveniência: segue valendo em memória até fechar a aba.
  }
}

/**
 * Define de quem são os rascunhos e carrega os salvos, descartando os vencidos.
 * Sem essa chamada o módulo opera só em memória.
 */
export function configurarRascunhos(usuarioId: number | string | null | undefined) {
  const nova = usuarioId != null ? `tera:rascunhos:${usuarioId}` : null;
  if (nova === chaveStorage) return;

  chaveStorage = nova;
  mapa = VAZIO;

  if (chaveStorage) {
    try {
      const bruto = localStorage.getItem(chaveStorage);
      const salvo: Mapa = bruto ? JSON.parse(bruto) : {};
      const limite = Date.now() - VALIDADE_MS;

      mapa = Object.fromEntries(
        Object.entries(salvo).filter(
          ([, r]) => r?.texto && r.atualizadoEm > limite,
        ),
      );

      if (Object.keys(mapa).length !== Object.keys(salvo).length) persistir();
    } catch {
      mapa = VAZIO;
    }
  }

  notificar();
}

export function lerRascunho(chave: string | null): string {
  return (chave && mapa[chave]?.texto) || '';
}

/** Texto vazio (ou só espaços) remove o rascunho em vez de guardar string vazia. */
export function salvarRascunho(chave: string | null, texto: string) {
  if (!chave) return;
  if (lerRascunho(chave) === texto) return;

  if (texto.trim()) {
    mapa = { ...mapa, [chave]: { texto, atualizadoEm: Date.now() } };
  } else {
    if (!(chave in mapa)) return;
    const { [chave]: _descartado, ...resto } = mapa;
    mapa = resto;
  }

  persistir();
  notificar();
}

export const limparRascunho = (chave: string | null) => salvarRascunho(chave, '');

function subscrever(fn: () => void) {
  ouvintes.add(fn);
  return () => {
    ouvintes.delete(fn);
  };
}

/** Mapa completo, para a lista marcar quais conversas têm rascunho. */
export function useRascunhos(): Mapa {
  return useSyncExternalStore(
    subscrever,
    () => mapa,
    () => VAZIO,
  );
}
